// src/pages/admin/ADPNetsuiteBulkSubmitFormPreview.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import ADPNetsuiteSubmitFormPreview from "./ADPNetsuiteSubmitFormPreview";

/**
 * Bulk preview: stacks one ADPNetsuiteSubmitFormPreview per ADP CO group in a
 * single scrollable modal and bubbles every group's payload up via
 * `onPayloadsChange` as `{ [adpCo]: payload | null }`.
 *
 * Props:
 *   groups            – { [adpCo: string]: rows[] }
 *   onPayloadsChange  – called with the latest payloads map after every change.
 *                       Parent uses it to know which groups are ready to submit.
 *   results           – optional { [adpCo]: { ok: bool, error?: string } } map
 *                       to surface per-group outcome in the header chip.
 *   submitting        – set of ADP CO keys currently in flight (for spinner).
 */
export default function ADPNetsuiteBulkSubmitFormPreview({
  groups,
  onPayloadsChange,
  results = {},
  submitting = new Set(),
  periodName,
  endDate,
}) {
  const entries = useMemo(
    () => Object.entries(groups || {}).sort(([a], [b]) => String(a).localeCompare(String(b))),
    [groups],
  );
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = {};
      for (const [adpCo] of entries) {
        next[adpCo] = prev[adpCo] ?? true;
      }
      return next;
    });
  }, [entries]);

  const isExpanded = useCallback((adpCo) => expandedGroups[adpCo] !== false, [expandedGroups]);

  const toggleGroup = useCallback((adpCo) => {
    setExpandedGroups((prev) => ({ ...prev, [adpCo]: !(prev[adpCo] !== false) }));
  }, []);

  const handleOne = useCallback(
    (adpCo, payload) => {
      onPayloadsChange?.((prev) => ({ ...(prev || {}), [adpCo]: payload }));
    },
    [onPayloadsChange],
  );

  if (entries.length === 0) {
    return <p className="text-sm text-palette-gray-secondary">No ADP CO groups to submit.</p>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-md bg-palette-indigo-soft border border-palette-indigo-border px-3 py-2 text-sm text-palette-indigo-primary">
        Reviewing <strong>{entries.length}</strong> journal{" "}
        {entries.length === 1 ? "entry" : "entries"} — one per ADP CO group. Each section below is a
        separate journal entry that will be submitted to NetSuite.
      </div>

      {entries.map(([adpCo, coRows]) => {
        const result = results[adpCo];
        const isSubmitting = submitting.has(adpCo);

        let chip = null;
        if (isSubmitting) {
          chip = (
            <span className="rounded-full bg-palette-indigo-muted text-palette-indigo-heading text-xs px-2 py-0.5 font-semibold">
              Sending…
            </span>
          );
        } else if (result?.ok === true) {
          chip = (
            <span className="rounded-full bg-palette-emerald-muted text-palette-emerald-hover text-xs px-2 py-0.5 font-semibold">
              ✓ Submitted
            </span>
          );
        } else if (result?.ok === false) {
          chip = (
            <span
              className="rounded-full bg-palette-red-muted text-palette-red-heading text-xs px-2 py-0.5 font-semibold"
              title={result.error || ""}
            >
              ✗ Failed
            </span>
          );
        }

        return (
          <section
            key={adpCo}
            className="rounded-lg border border-palette-gray-border bg-white shadow-sm p-4 space-y-3"
          >
            <header className="flex items-center justify-between gap-2 border-b border-palette-gray-divider pb-2">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  type="button"
                  onClick={() => toggleGroup(adpCo)}
                  className="flex items-center gap-2 min-w-0 text-left"
                  aria-expanded={isExpanded(adpCo)}
                >
                  <span className="text-sm font-bold text-palette-gray-text truncate">
                    ADP CO: {adpCo}
                  </span>
                  <span className="text-xs text-palette-gray-secondary whitespace-nowrap">
                    {coRows.length} row{coRows.length !== 1 ? "s" : ""}
                  </span>
                  <span className="text-xs text-palette-gray-secondary whitespace-nowrap">
                    {isExpanded(adpCo) ? "▲ Close" : "▼ Expand"}
                  </span>
                </button>
              </div>
              {chip}
            </header>

            {isExpanded(adpCo) ? (
              <ADPNetsuiteSubmitFormPreview
                preview={{ adpCo, rows: coRows }}
                onPayloadChange={(payload) => handleOne(adpCo, payload)}
                periodName={periodName}
                endDate={endDate}
              />
            ) : (
              <div className="rounded-md bg-palette-gray-soft border border-palette-gray-divider px-3 py-2 text-sm text-palette-gray-badge">
                Collapsed. Click the header to expand and review this journal entry.
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
